import { z } from 'zod';
import { uuidSchema, nullableDateSchema } from './common.schema';

// ── ADT-A31 Request Schema ──────────────────────────────────────────────────
// Represents the parsed HL7 ADT^A31 payload (demographics update, no location change)

/**
 * Patient demographics from HL7 PID segment.
 */
export const AdtA31PatientDemographicsSchema = z.object({
  first_name: z.string().max(100).optional(),
  middle_name: z.string().max(100).optional().nullable(),
  last_name: z.string().max(100).optional(),
  date_of_birth: z.string().datetime({ offset: true, local: true }).optional(),
  gender: z.string().max(20).optional().nullable(),
  birth_weight_grams: z.number().positive().optional().nullable(),
  gestational_age_weeks: z.number().positive().optional().nullable(),
  multiple_birth_ind: z.boolean().optional(),
  multiple_birth_ord: z.number().int().positive().optional().nullable(),
  deceased_ind: z.boolean().optional(),
  deceased_dt: nullableDateSchema,
});

/**
 * Mother/Guardian demographics from HL7 NK1 segment.
 */
export const AdtA31MotherGuardianSchema = z.object({
  first_name: z.string().max(100).optional(),
  last_name: z.string().max(100).optional(),
  date_of_birth: z.string().datetime({ offset: true, local: true }).optional().nullable(),
  phone: z.string().max(30).optional().nullable(),
  email: z.string().email().max(254).optional().nullable(),
  street_address: z.string().max(300).optional().nullable(),
  city: z.string().max(100).optional().nullable(),
  zip_code: z.string().max(20).optional().nullable(),
  relationship_type: z.string().max(20).optional(),
});

/**
 * ADT-A31 ingest request body.
 * Sent by the integration engine after receiving an HL7 ADT^A31 message.
 */
export const AdtA31RequestSchema = z.object({
  /** Hospital where the message originated — injected server-side from env */
  hospital_id: uuidSchema.optional(),
  /** Patient MRN from HL7 PID-3 */
  patient_mrn: z.string().min(1).max(50),
  /** HL7 message type (should be ADT^A31) */
  hl7_message_type: z.string().max(20).default('ADT^A31'),
  /** HL7 version */
  hl7_version: z.string().max(10).optional(),
  /** HL7 message control ID for deduplication */
  hl7_message_control_id: z.string().max(50).optional(),
  /** Sending application identifier */
  sending_application: z.string().max(50).optional().default(''),
  /** Sending facility identifier */
  sending_facility: z.string().max(50).optional().default(''),
  /** Raw HL7 payload for audit trail */
  raw_payload: z.string(),
  /** Event timestamp from EVN-2 */
  event_timestamp: z.string().datetime({ offset: true, local: true }).optional(),
  /** Patient demographics to update (from PID segment) */
  patient_demographics: AdtA31PatientDemographicsSchema.optional(),
  /** Mother/Guardian info to update (from NK1 segment) */
  mother_guardian: AdtA31MotherGuardianSchema.optional(),
});

export type AdtA31Request = z.infer<typeof AdtA31RequestSchema>;

// ── ADT-A31 Response Schema ─────────────────────────────────────────────────

export const AdtA31ResponseSchema = z.object({
  message_id: uuidSchema,
  event_id: uuidSchema,
  resolution_status: z.enum(['MATCHED', 'SKIPPED']),
  resolution_notes: z.string().optional(),
  patient_id: uuidSchema.optional(),
});

export type AdtA31Response = z.infer<typeof AdtA31ResponseSchema>;

// ── ADT-A17 Request Schema ──────────────────────────────────────────────────
// Represents the parsed HL7 ADT^A17 payload (swap bed assignments between two patients)

/**
 * ADT-A17 ingest request body.
 * Contains two patient MRNs whose Facility/Unit/Room/Bed assignments must be swapped.
 */
export const AdtA17RequestSchema = z.object({
  /** Hospital where the message originated — injected server-side from env */
  hospital_id: uuidSchema.optional(),
  /** First patient MRN from HL7 PID-3 — can be omitted when raw_payload is provided */
  patient_mrn_1: z.string().max(50).optional(),
  /** Second patient MRN from HL7 PID-3 (second PID segment) — can be omitted when raw_payload is provided */
  patient_mrn_2: z.string().max(50).optional(),
  /** HL7 message type (should be ADT^A17) */
  hl7_message_type: z.string().max(20).default('ADT^A17'),
  /** HL7 version */
  hl7_version: z.string().max(10).optional(),
  /** HL7 message control ID for deduplication */
  hl7_message_control_id: z.string().max(50).optional(),
  /** Sending application identifier */
  sending_application: z.string().max(50).optional().default(''),
  /** Sending facility identifier */
  sending_facility: z.string().max(50).optional().default(''),
  /** Raw HL7 payload for audit trail */
  raw_payload: z.string(),
  /** Event timestamp from EVN-2 */
  event_timestamp: z.string().datetime({ offset: true, local: true }).optional(),
});

export type AdtA17Request = z.infer<typeof AdtA17RequestSchema>;

// ── ADT-A17 Response Schema ─────────────────────────────────────────────────

export const AdtA17ResponseSchema = z.object({
  message_id: uuidSchema,
  event_id_1: uuidSchema,
  event_id_2: uuidSchema,
  resolution_status: z.enum(['MATCHED', 'SKIPPED']),
  resolution_notes: z.string().optional(),
  patient_id_1: uuidSchema.optional(),
  patient_id_2: uuidSchema.optional(),
});

export type AdtA17Response = z.infer<typeof AdtA17ResponseSchema>;

// ── ADT Cancel Events Request Schema ────────────────────────────────────────
// Covers ADT^A11 (Cancel Admit), ADT^A12 (Cancel Transfer),
// ADT^A13 (Cancel Discharge), ADT^A38 (Cancel Pre-admit)

/**
 * ADT Cancel event ingest request body.
 * Sent by the integration engine for cancel-type ADT messages.
 */
export const AdtCancelRequestSchema = z.object({
  /** Hospital where the message originated — injected server-side from env */
  hospital_id: uuidSchema.optional(),
  /** Patient MRN from HL7 PID-3 */
  patient_mrn: z.string().min(1).max(50),
  /** Cancel event type */
  cancel_type: z.enum(['A11', 'A12', 'A13', 'A38']),
  /** HL7 message type (e.g. ADT^A11) */
  hl7_message_type: z.string().max(20),
  /** HL7 version */
  hl7_version: z.string().max(10).optional(),
  /** HL7 message control ID for deduplication */
  hl7_message_control_id: z.string().max(50).optional(),
  /** Sending application identifier */
  sending_application: z.string().max(50).optional().default(''),
  /** Sending facility identifier */
  sending_facility: z.string().max(50).optional().default(''),
  /** Raw HL7 payload for audit trail */
  raw_payload: z.string(),
  /** Event timestamp from EVN-2 */
  event_timestamp: z.string().datetime({ offset: true, local: true }).optional(),
});

export type AdtCancelRequest = z.infer<typeof AdtCancelRequestSchema>;

// ── ADT Cancel Events Response Schema ───────────────────────────────────────

export const AdtCancelResponseSchema = z.object({
  message_id: uuidSchema,
  event_id: uuidSchema,
  cancel_type: z.enum(['A11', 'A12', 'A13', 'A38']),
  resolution_status: z.enum(['MATCHED', 'SKIPPED']),
  resolution_notes: z.string().optional(),
  patient_id: uuidSchema.optional(),
});

export type AdtCancelResponse = z.infer<typeof AdtCancelResponseSchema>;

// ── ADT-A03 Request Schema ──────────────────────────────────────────────────
// Represents the parsed HL7 ADT^A03 payload (patient discharge)

/**
 * ADT-A03 ingest request body.
 * Sent by the integration engine after receiving an HL7 ADT^A03 message.
 */
export const AdtA03RequestSchema = z.object({
  /** Hospital where the message originated — injected server-side from env */
  hospital_id: uuidSchema.optional(),
  /** Patient MRN from HL7 PID-3 */
  patient_mrn: z.string().min(1).max(50),
  /** HL7 message type (should be ADT^A03) */
  hl7_message_type: z.string().max(20).default('ADT^A03'),
  /** HL7 version */
  hl7_version: z.string().max(10).optional(),
  /** HL7 message control ID for deduplication */
  hl7_message_control_id: z.string().max(50).optional(),
  /** Sending application identifier */
  sending_application: z.string().max(50).optional().default(''),
  /** Sending facility identifier */
  sending_facility: z.string().max(50).optional().default(''),
  /** Raw HL7 payload for audit trail */
  raw_payload: z.string(),
  /** Event timestamp from EVN-2 (used as fallback discharge time) */
  event_timestamp: z.string().datetime({ offset: true, local: true }).optional(),
  /** Discharge date/time from PV1-45 */
  discharge_date: z.string().datetime({ offset: true, local: true }).optional(),
  /** Discharge disposition from PV1-36 */
  discharge_disposition: z.string().max(30).optional().nullable(),
});

export type AdtA03Request = z.infer<typeof AdtA03RequestSchema>;

// ── ADT-A03 Response Schema ─────────────────────────────────────────────────

export const AdtA03ResponseSchema = z.object({
  message_id: uuidSchema,
  event_id: uuidSchema,
  resolution_status: z.enum(['MATCHED', 'SKIPPED']),
  resolution_notes: z.string().optional(),
  patient_id: uuidSchema.optional(),
});

export type AdtA03Response = z.infer<typeof AdtA03ResponseSchema>;

// ── ADT Inbound Message Row Schema ──────────────────────────────────────────

export const AdtInboundMessageRowSchema = z.object({
  message_id: uuidSchema,
  channel_id: uuidSchema.nullable().optional(),
  hospital_id: uuidSchema,
  sending_application: z.string(),
  sending_facility: z.string(),
  hl7_message_type: z.string(),
  hl7_version: z.string().nullable().optional(),
  hl7_message_control_id: z.string().nullable().optional(),
  raw_payload: z.string(),
  processing_status: z.string(),
  error_details: z.string().nullable().optional(),
  received_at: z.string().datetime(),
  processed_at: z.string().datetime().nullable().optional(),
  created_at: z.string().datetime(),
});

export type AdtInboundMessageRow = z.infer<typeof AdtInboundMessageRowSchema>;

// ── ADT Event Row Schema ────────────────────────────────────────────────────

export const AdtEventRowSchema = z.object({
  event_id: uuidSchema,
  message_id: uuidSchema.nullable().optional(),
  hospital_id: uuidSchema,
  event_type: z.string(),
  patient_mrn: z.string().nullable().optional(),
  visit_number: z.string().nullable().optional(),
  ward_code: z.string().nullable().optional(),
  bed_code: z.string().nullable().optional(),
  attending_doctor: z.string().nullable().optional(),
  event_timestamp: z.string().datetime().nullable().optional(),
  resolved_patient_id: uuidSchema.nullable().optional(),
  resolved_encounter_id: uuidSchema.nullable().optional(),
  resolution_status: z.string(),
  resolution_notes: z.string().nullable().optional(),
  created_at: z.string().datetime(),
});

export type AdtEventRow = z.infer<typeof AdtEventRowSchema>;

// ── ADT Admit/Register Request Schema ───────────────────────────────────────
// Covers ADT^A01 (Admit), ADT^A04 (Register), ADT^A05 (Pre-admit), ADT^A08 (Update Patient Info)

/**
 * Patient demographics for admit/register from HL7 PID segment.
 */
export const AdtAdmitPatientSchema = z.object({
  first_name: z.string().min(1).max(100),
  last_name: z.string().min(1).max(100),
  middle_name: z.string().max(100).optional().nullable(),
  date_of_birth: z.string().datetime({ offset: true, local: true }),
  gender: z.string().max(20),
  birth_weight_grams: z.number().positive().optional().nullable(),
  gestational_age_weeks: z.number().positive().optional().nullable(),
  multiple_birth_ind: z.boolean().optional(),
  multiple_birth_ord: z.number().int().positive().optional().nullable(),
});

/**
 * Mother/Donor demographics from HL7 NK1 segment.
 */
export const AdtAdmitMotherDonorSchema = z.object({
  /** Mother identifier (NK1-33) used to link siblings to the same mother record */
  mother_identifier: z.string().max(50).optional().nullable(),
  first_name: z.string().max(100).optional(),
  last_name: z.string().max(100).optional(),
  date_of_birth: z.string().datetime({ offset: true, local: true }).optional().nullable(),
  phone: z.string().max(30).optional().nullable(),
  email: z.string().email().max(254).optional().nullable(),
  street_address: z.string().max(300).optional().nullable(),
  city: z.string().max(100).optional().nullable(),
  zip_code: z.string().max(20).optional().nullable(),
  relationship_type: z.string().max(20).optional(),
});

/**
 * Location assignment from HL7 PV1 segment.
 */
export const AdtAdmitLocationSchema = z.object({
  /** Facility (PV1-3.4 or mapped) */
  facility_id: uuidSchema.optional(),
  /** Unit/Ward (PV1-3.1) — required for location update */
  unit_id: uuidSchema.optional(),
  /** Bed (PV1-3.3) */
  bed_id: uuidSchema.optional(),
});

/**
 * ADT Admit/Register ingest request body.
 * Sent by the integration engine for ADT^A01, ADT^A04, ADT^A05, ADT^A08 messages.
 */
export const AdtAdmitRequestSchema = z.object({
  /** Hospital where the message originated — injected server-side from env */
  hospital_id: uuidSchema.optional(),
  /** Patient MRN from HL7 PID-3 */
  patient_mrn: z.string().min(1).max(50),
  /** Trigger event type */
  trigger_event: z.enum(['A01', 'A04', 'A05', 'A08']),
  /** HL7 message type (e.g. ADT^A01) */
  hl7_message_type: z.string().max(20),
  /** HL7 version */
  hl7_version: z.string().max(10).optional(),
  /** HL7 message control ID for deduplication */
  hl7_message_control_id: z.string().max(50).optional(),
  /** Sending application identifier */
  sending_application: z.string().max(50).optional().default(''),
  /** Sending facility identifier */
  sending_facility: z.string().max(50).optional().default(''),
  /** Raw HL7 payload for audit trail */
  raw_payload: z.string(),
  /** Event timestamp from EVN-2 */
  event_timestamp: z.string().datetime({ offset: true, local: true }).optional(),
  /** Encounter identifier from PV1-19 or similar */
  encounter_number: z.string().max(100).optional(),
  /** Patient demographics (from PID segment) */
  patient_demographics: AdtAdmitPatientSchema,
  /** Mother/Donor info (from NK1 segment) */
  mother_donor: AdtAdmitMotherDonorSchema.optional(),
  /** Location assignment (from PV1 segment) */
  location: AdtAdmitLocationSchema.optional(),
});

export type AdtAdmitRequest = z.infer<typeof AdtAdmitRequestSchema>;

// ── ADT Admit/Register Response Schema ──────────────────────────────────────

export const AdtAdmitResponseSchema = z.object({
  message_id: uuidSchema,
  event_id: uuidSchema,
  trigger_event: z.enum(['A01', 'A04', 'A05', 'A08']),
  resolution_status: z.enum(['CREATED', 'UPDATED', 'SKIPPED', 'READMITTED']),
  resolution_notes: z.string().optional(),
  patient_id: uuidSchema.optional(),
  encounter_id: uuidSchema.optional(),
});

export type AdtAdmitResponse = z.infer<typeof AdtAdmitResponseSchema>;

// ── ADT Transfer Request Schema ─────────────────────────────────────────────
// Covers ADT^A02 (Transfer), ADT^A06 (Transfer – Outpatient), ADT^A07 (Transfer – Inpatient)

/**
 * Patient demographics for creating new patient from transfer (minimal set).
 */
export const AdtTransferPatientSchema = z.object({
  first_name: z.string().min(1).max(100),
  last_name: z.string().min(1).max(100),
  middle_name: z.string().max(100).optional().nullable(),
  date_of_birth: z.string().datetime({ offset: true, local: true }),
  gender: z.string().max(20),
});

/**
 * Location from HL7 PV1 segment for transfer events.
 */
export const AdtTransferLocationSchema = z.object({
  /** Facility (PV1-3.4 or mapped) */
  facility_id: uuidSchema,
  /** Unit/Ward (PV1-3.1) */
  unit_id: uuidSchema.optional(),
  /** Bed (PV1-3.3) */
  bed_id: uuidSchema.optional(),
});

/**
 * ADT Transfer ingest request body.
 * Sent by the integration engine for ADT^A02, ADT^A06, ADT^A07 messages.
 */
export const AdtTransferRequestSchema = z.object({
  /** Hospital where the message originated — injected server-side from env */
  hospital_id: uuidSchema.optional(),
  /** Patient MRN from HL7 PID-3 */
  patient_mrn: z.string().min(1).max(50),
  /** Trigger event type */
  trigger_event: z.enum(['A02', 'A06', 'A07']),
  /** HL7 message type (e.g. ADT^A02) */
  hl7_message_type: z.string().max(20),
  /** HL7 version */
  hl7_version: z.string().max(10).optional(),
  /** HL7 message control ID for deduplication */
  hl7_message_control_id: z.string().max(50).optional(),
  /** Sending application identifier */
  sending_application: z.string().max(50).optional().default(''),
  /** Sending facility identifier */
  sending_facility: z.string().max(50).optional().default(''),
  /** Raw HL7 payload for audit trail */
  raw_payload: z.string(),
  /** Event timestamp from EVN-2 */
  event_timestamp: z.string().datetime({ offset: true, local: true }).optional(),
  /** Whether this is a temporary transfer (EVN-6 or PV1-2='P') */
  is_temporary: z.boolean().optional().default(false),
  /** Location assignment (from PV1 segment) */
  location: AdtTransferLocationSchema.optional(),
  /** Patient demographics — needed only if MRN not found (create new patient) */
  patient_demographics: AdtTransferPatientSchema.optional(),
});

export type AdtTransferRequest = z.infer<typeof AdtTransferRequestSchema>;

// ── ADT Transfer Response Schema ────────────────────────────────────────────

export const AdtTransferResponseSchema = z.object({
  message_id: uuidSchema,
  event_id: uuidSchema,
  trigger_event: z.enum(['A02', 'A06', 'A07']),
  resolution_status: z.enum(['CREATED', 'UPDATED', 'SKIPPED']),
  resolution_notes: z.string().optional(),
  patient_id: uuidSchema.optional(),
});

export type AdtTransferResponse = z.infer<typeof AdtTransferResponseSchema>;
